import * as t from 'io-ts';
import {isUndefined} from 'lodash';

import {DeepReadonly} from '../../../../../libs/shared/helpers/src/typescript/typeHelpers';
import {RActivityIdRef} from '../../../../models/api/activityModel';
import {
  makeEnumRuntimeType,
  RConversationIdRef,
  RInboxIdRef,
  RMessageUidRef,
} from '../../../../models/api/apiTypes';
import {RConversationSortProperty} from '../../../../models/api/conversationCommonTypes';
import {RMessageSortProperty} from '../../../../models/api/messageModel';
import {RCellsSortDirection} from '../../../../models/api/sortOptionModel';
import {
  RGlobalFolder,
  RInboxesFilter,
  RInboxesSearchLocation,
  RTeamFolder,
  RTeammateFolder,
  RTeammateMessageFolder,
} from '../../../../models/url/inboxesUrlModel';

/*
 * Runtime types.
 */

export enum InboxesParametersTypesEnum {
  INBOX = 'INBOX',
  TEAMMATE_INBOX = 'TEAMMATE_INBOX',
  VIEW = 'VIEW',
  TEAMMATE_VIEW = 'TEAMMATE_VIEW',
  // A teammate's private lens that consists of inboxes from a given team. It lives in the teammate's private namespace.
  TEAMMATE_LENS = 'TEAMMATE_LENS',
  // A shared lens that consists of inboxes from a given team. It lives in the team namespace.
  TEAM_LENS = 'TEAM_LENS',
  TEAMMATE_MESSAGES = 'TEAMMATE_MESSAGES',
  USER = 'USER',
  TEAMMATE = 'TEAMMATE',
  TEAM = 'TEAM',
  GLOBAL = 'GLOBAL',
  TOPIC = 'TOPIC',
}
const RInboxesParametersType = makeEnumRuntimeType(InboxesParametersTypesEnum);

const RInboxesParametersIdActivity = t.type({
  id: RActivityIdRef,
});

export enum InboxesSelectionTypesEnum {
  NORMAL = 'NORMAL',
  ALL_EXCEPT = 'ALL_EXCEPT',
}

function makeNormalInboxesSelectionParameters<T extends t.Mixed>(idsType: T) {
  return t.type({
    type: t.literal(InboxesSelectionTypesEnum.NORMAL),
    selectedIds: idsType,
  });
}

function makeInboxesSelectionParameters<T extends t.Mixed>(idType: T) {
  return t.taggedUnion('type', [
    makeNormalInboxesSelectionParameters(t.readonlyArray(idType)),
    t.type({
      type: t.literal(InboxesSelectionTypesEnum.ALL_EXCEPT),
      deselectedIds: t.readonlyArray(idType),
    }),
  ]);
}

export const RInboxesConversationSelectionParameters = makeInboxesSelectionParameters(RConversationIdRef);
const RInboxesMessagesSelectionParameters = makeInboxesSelectionParameters(RMessageUidRef);

const RInboxesParametersDateActivity = t.type({
  date: t.number,
});

export const RInboxesParametersActivity = t.union([
  RInboxesParametersIdActivity,
  RInboxesParametersDateActivity,
]);

const RInboxesParametersBase = t.intersection([
  t.type({
    type: RInboxesParametersType,
    sortDirection: RCellsSortDirection,
  }),
  t.partial({
    activity: RInboxesParametersActivity,
  }),
]);

const RInboxesSearchParameters = t.intersection([
  t.type({
    location: RInboxesSearchLocation,
    query: t.string,
  }),
  t.partial({
    fromSelection: RInboxesConversationSelectionParameters,
  }),
]);

const RInboxesParameterConversationsBase = t.intersection([
  RInboxesParametersBase,
  t.type({
    filters: t.readonlyArray(RInboxesFilter),
    sortProperty: RConversationSortProperty,
    selection: RInboxesConversationSelectionParameters,
  }),
  t.partial({
    search: RInboxesSearchParameters,
  }),
]);

const RInboxesInboxParameters = t.intersection([
  RInboxesParameterConversationsBase,
  t.type({
    type: t.literal(InboxesParametersTypesEnum.INBOX),
    inboxId: RInboxIdRef,
  }),
]);

const RInboxesTeammateInboxParameters = t.intersection([
  RInboxesParameterConversationsBase,
  t.type({
    type: t.literal(InboxesParametersTypesEnum.TEAMMATE_INBOX),
    inboxId: RInboxIdRef,
  }),
]);

const RInboxesViewParameters = t.intersection([
  RInboxesParameterConversationsBase,
  t.type({
    type: t.literal(InboxesParametersTypesEnum.VIEW),
    viewId: t.Integer,
  }),
]);

const RInboxesTeammateViewParameters = t.intersection([
  RInboxesParameterConversationsBase,
  t.type({
    type: t.literal(InboxesParametersTypesEnum.TEAMMATE_VIEW),
    viewId: t.Integer,
  }),
]);

const RInboxesTeamLensParameters = t.intersection([
  RInboxesParameterConversationsBase,
  t.type({
    type: t.literal(InboxesParametersTypesEnum.TEAM_LENS),
    lensId: t.Integer,
  }),
]);

const RInboxesTeammateLensParameters = t.intersection([
  RInboxesParameterConversationsBase,
  t.type({
    type: t.literal(InboxesParametersTypesEnum.TEAMMATE_LENS),
    lensId: t.Integer,
  }),
]);

const RInboxesUserParameters = t.intersection([
  RInboxesParameterConversationsBase,
  t.type({
    type: t.literal(InboxesParametersTypesEnum.USER),
    teammateId: t.Integer,
    folder: RTeammateFolder,
  }),
]);

const RInboxesTeammateParameters = t.intersection([
  RInboxesParameterConversationsBase,
  t.type({
    type: t.literal(InboxesParametersTypesEnum.TEAMMATE),
    teammateId: t.Integer,
    folder: RTeammateFolder,
  }),
]);

const RInboxesTeamParameters = t.intersection([
  RInboxesParameterConversationsBase,
  t.type({
    type: t.literal(InboxesParametersTypesEnum.TEAM),
    teamId: t.Integer,
    folder: RTeamFolder,
  }),
]);

const RInboxesGlobalParameters = t.intersection([
  RInboxesParameterConversationsBase,
  t.type({
    type: t.literal(InboxesParametersTypesEnum.GLOBAL),
    folder: RGlobalFolder,
  }),
]);

export const RInboxesConversationsParameters = t.taggedUnion('type', [
  RInboxesInboxParameters,
  RInboxesTeammateInboxParameters,
  RInboxesViewParameters,
  RInboxesTeammateViewParameters,
  RInboxesTeamLensParameters,
  RInboxesTeammateLensParameters,
  RInboxesUserParameters,
  RInboxesTeammateParameters,
  RInboxesTeamParameters,
  RInboxesGlobalParameters,
]);

export const RInboxesTeammateMessagesParameters = t.intersection([
  RInboxesParametersBase,
  t.type({
    type: t.literal(InboxesParametersTypesEnum.TEAMMATE_MESSAGES),
    teammateId: t.Integer,
    folder: RTeammateMessageFolder,
    sortProperty: RMessageSortProperty,
    selection: RInboxesMessagesSelectionParameters,
    hasFindMyDrafts2: t.boolean,
    hasListSendingMessagesInSentFolder: t.boolean,
  }),
]);

export const RInboxesParameters = t.taggedUnion('type', [
  RInboxesConversationsParameters,
  RInboxesTeammateMessagesParameters,
]);

/*
 * Types.
 */

export type InboxesConversationSelectionParameters = DeepReadonly<
  t.TypeOf<typeof RInboxesConversationSelectionParameters>
>;
export type InboxesMessagesSelectionParameters = DeepReadonly<
  t.TypeOf<typeof RInboxesMessagesSelectionParameters>
>;
export type InboxesSelectionParameters =
  | InboxesConversationSelectionParameters
  | InboxesMessagesSelectionParameters;

export type InboxesParametersActivity = DeepReadonly<t.TypeOf<typeof RInboxesParametersActivity>>;
export interface InboxesSearchParameters extends DeepReadonly<t.TypeOf<typeof RInboxesSearchParameters>> {}

export interface InboxesInboxParameters extends DeepReadonly<t.TypeOf<typeof RInboxesInboxParameters>> {}
export interface InboxesTeammateInboxParameters
  extends DeepReadonly<t.TypeOf<typeof RInboxesTeammateInboxParameters>> {}
export interface InboxesViewParameters extends DeepReadonly<t.TypeOf<typeof RInboxesViewParameters>> {}
export interface InboxesTeammateViewParameters
  extends DeepReadonly<t.TypeOf<typeof RInboxesTeammateViewParameters>> {}
export interface InboxesTeammateLensParameters
  extends DeepReadonly<t.TypeOf<typeof RInboxesTeammateLensParameters>> {}
export interface InboxesTeamLensParameters
  extends DeepReadonly<t.TypeOf<typeof RInboxesTeamLensParameters>> {}
export interface InboxesTeammateMessagesParameters
  extends DeepReadonly<t.TypeOf<typeof RInboxesTeammateMessagesParameters>> {}
export interface InboxesUserParameters extends DeepReadonly<t.TypeOf<typeof RInboxesUserParameters>> {}
export interface InboxesTeammateParameters
  extends DeepReadonly<t.TypeOf<typeof RInboxesTeammateParameters>> {}
export interface InboxesTeamParameters extends DeepReadonly<t.TypeOf<typeof RInboxesTeamParameters>> {}
export interface InboxesGlobalParameters extends DeepReadonly<t.TypeOf<typeof RInboxesGlobalParameters>> {}

export type InboxesConversationsParameters =
  | InboxesInboxParameters
  | InboxesTeammateInboxParameters
  | InboxesViewParameters
  | InboxesTeammateViewParameters
  | InboxesTeamLensParameters
  | InboxesTeammateLensParameters
  | InboxesUserParameters
  | InboxesTeammateParameters
  | InboxesTeamParameters
  | InboxesGlobalParameters;

export type InboxesConversationsParametersWithSearch = InboxesConversationsParameters & {
  readonly search: InboxesSearchParameters;
};

export type InboxesParameters = InboxesConversationsParameters | InboxesTeammateMessagesParameters;

/*
 * Type guards.
 */

export function isInboxesConversationsParameters(
  parameters: InboxesParameters,
): parameters is InboxesConversationsParameters {
  return parameters.type !== InboxesParametersTypesEnum.TEAMMATE_MESSAGES;
}

export function hasSearchInInboxesParameters(
  parameters: InboxesParameters,
): parameters is InboxesConversationsParametersWithSearch {
  return (
    isInboxesConversationsParameters(parameters) && 'search' in parameters && !isUndefined(parameters.search)
  );
}
